/* eslint-disable max-lines */
/**
 * @deprecated This file is deprecated and kept only for backward compatibility.
 * Please use useUserState and useOrganizationState directly instead.
 *
 * Migration guide:
 * - User data: import { useUserState } from '@/state/userState'
 * - Organization data: import { useOrganizationState } from '@/state/organizationState'
 *
 * This compatibility layer will be removed in v2.0.0
 */

import { User } from 'firebase/auth';
import { Timestamp } from 'firebase/firestore';
import { create } from 'zustand';

import { featuresAppType, useOrganizationState } from './organizationState';
import { UserRole, useUserState } from './userState';

export type { featuresAppType, UserRole };

interface AuthState {
  currentUser: User | null;
  isLoading: boolean;
  orgResolved: boolean;
  signInWithEmail: (email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;

  // User data
  organizationId: string | null;
  avatar: string | null;
  userName: string | null;
  userEmail: string | null;
  role: UserRole;
  userLanguage: string | null;

  // Organization data
  defaultLang: string;
  organizations: string[] | null;
  testExpirationDate: Timestamp | null;
  languages: string[] | null;
  code: string | null;
  webAppUrl: string | null;
  featuresApp: featuresAppType[] | null;
  accountManager: string | null;

  setAvatar: (avatar: string | null) => void;
}

export const useAuthState = create<AuthState>((set) => {
  // Subscribe to both stores and merge their state
  const syncStates = () => {
    const userState = useUserState.getState();
    const orgState = useOrganizationState.getState();

    set({
      // User data
      currentUser: userState.currentUser,
      userEmail: userState.userEmail,
      userName: userState.userName,
      avatar: userState.avatar,
      role: userState.role,
      userLanguage: userState.userLanguage,
      organizationId: userState.organizationId,
      isLoading: userState.isLoading || orgState.isLoading,
      orgResolved: userState.isResolved && orgState.isResolved,

      // Organization data
      defaultLang: orgState.defaultLang,
      organizations: orgState.organizations,
      testExpirationDate: orgState.testExpirationDate,
      languages: orgState.languages,
      code: orgState.code,
      webAppUrl: orgState.webAppUrl,
      featuresApp: orgState.featuresApp,
      accountManager: orgState.accountManager,
    });
  };

  // Initial sync
  syncStates();

  // Subscribe to user state changes
  useUserState.subscribe(() => {
    syncStates();
  });

  // Subscribe to organization state changes
  useOrganizationState.subscribe(() => {
    syncStates();
  });

  return {
    // Initial state (will be immediately overridden by syncStates)
    currentUser: null,
    isLoading: true,
    orgResolved: false,
    organizationId: null,
    avatar: null,
    userName: null,
    userEmail: null,
    role: null,
    userLanguage: null,
    defaultLang: 'pl',
    organizations: null,
    testExpirationDate: null,
    languages: null,
    code: null,
    webAppUrl: null,
    featuresApp: null,
    accountManager: null,

    // Delegate actions to underlying stores
    signInWithEmail: async (email: string, password: string) => useUserState.getState().signInWithEmail(email, password),

    logout: async () => useUserState.getState().logout(),

    setAvatar: (avatar: string | null) => {
      useUserState.getState().setAvatar(avatar);
    },
  };
});
